import React from 'react';
import {useQuery} from '@apollo/client';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, Empty, Image, Skeleton, Tag, Timeline} from 'antd';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {
    GET_PARTNER_PENALTY_DETAIL,
    GET_WAREHOUSE_PENALTY_DETAIL,
    GetPartnerPenaltyDetailResponse,
    GetPartnerPenaltyDetailVariables,
} from './graphql/queries';
import {PenaltyCenterMode} from './graphql/types';
import {formatIsoDateTime} from './utils/penaltyCenterUtils';
import PenaltyBasicInfo, {Field, SectionTitle, STATUS_COLOR} from './components/PenaltyBasicInfo';
import type {TFunction} from 'i18next';

/**
 * 渲染操作日志描述，支持三种格式：
 *  1. 纯 i18n key：直接翻译，找不到 key 则原样显示
 *  2. "key::param" 格式：翻译 key 并插值 {{reason}}，找不到 key 则显示 param
 *  3. 旧版中文字符串（无 key）：原样显示
 */
function renderOperationLogDesc(description: string | null | undefined, t: TFunction): string {
    if (!description) return '';
    const sep = description.indexOf('::');
    if (sep === -1) {
        return t(description, { defaultValue: description });
    }
    const key = description.substring(0, sep);
    const param = description.substring(sep + 2);
    return t(key, { reason: param, defaultValue: param });
}

interface PenaltyDetailPageProps {
    /** 'warehouse' 走仓库侧接口并多传 warehouseId；默认 DSP 侧 */
    mode?: PenaltyCenterMode;
}

/**
 * 罚单详情。
 * DSP 侧数据隔离由后端做：查他人罚单直接报错，前端不传 dspId。
 * warehouse 侧改传 warehouseId，后端校验「该罚单属于该仓」，防止直接改 URL 越仓看详情。
 * 操作记录随详情一并返回，不再单独查 partnerPenaltyOperationLogs。
 */
const PenaltyDetailPage: React.FC<PenaltyDetailPageProps> = ({mode = 'dsp'}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {id} = useParams<{id: string}>();
    const {currentWarehouse} = useWarehouseContext();
    const isWarehouse = mode === 'warehouse';
    const warehouseId = Number(currentWarehouse?.id ?? 0);
    // 路由参数是字符串，非法值（如手输的 /abc）不发请求，避免后端参数校验报错
    const penaltyId = Number(id);
    const validId = Number.isInteger(penaltyId) && penaltyId > 0;
    // 仓库未选定时不发请求，否则会打出 warehouseId=0 的无效查询
    const canQuery = validId && (!isWarehouse || warehouseId > 0);

    const {data, loading} = useQuery<
        GetPartnerPenaltyDetailResponse,
        GetPartnerPenaltyDetailVariables & {warehouseId?: number}
    >(
        isWarehouse ? GET_WAREHOUSE_PENALTY_DETAIL : GET_PARTNER_PENALTY_DETAIL,
        {
            variables: isWarehouse ? {id: penaltyId, warehouseId} : {id: penaltyId},
            skip: !canQuery,
            fetchPolicy: 'network-only',
        }
    );
    const detail = data?.partnerPenaltyDetail;

    if (!validId) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
                <h1 className="text-2xl font-bold m-0">
                    {t('partnerControl.penaltyCenter.detail.title')}
                </h1>
                {detail && (
                    <>
                        <Tag>{detail.penaltyNo}</Tag>
                        <Tag color={STATUS_COLOR[detail.status] ?? 'default'}>
                            {t(`partnerControl.penaltyCenter.status.${detail.status}`, {
                                defaultValue: detail.status,
                            })}
                        </Tag>
                    </>
                )}
            </div>

            {loading || !detail ? (
                <div className="bg-white rounded-lg p-6">
                    <Skeleton active paragraph={{rows: 8}} />
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <SectionTitle title={t('partnerControl.penaltyCenter.detail.basicInfo')} />
                        <PenaltyBasicInfo detail={detail} mode={mode} />
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <SectionTitle title={t('partnerControl.penaltyCenter.detail.appealInfo')} />
                        <div className="text-xs text-gray-500 mb-2">
                            {t('partnerControl.penaltyCenter.detail.appealTime')}：
                            {formatIsoDateTime(detail.appealTime)}
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-900 mb-3">
                            {detail.appealReason || t('partnerControl.penaltyCenter.detail.noAppeal')}
                        </div>
                        {/* 多图平铺，点开可在同一预览组里左右翻页 */}
                        <Image.PreviewGroup>
                            <div className="flex flex-wrap gap-2">
                                {detail.evidenceImageUrls.map((url) => (
                                    <Image
                                        key={url}
                                        src={url}
                                        width={96}
                                        height={96}
                                        className="rounded object-cover"
                                    />
                                ))}
                            </div>
                        </Image.PreviewGroup>
                        {detail.rejectReason && (
                            <div className="mt-3">
                                <Field
                                    label={t('partnerControl.penaltyCenter.detail.rejectReason')}
                                    value={detail.rejectReason}
                                    valueClassName="text-sm text-red-500"
                                />
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <SectionTitle title={t('partnerControl.penaltyCenter.logs.title')} />
                        {detail.operationLogs.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <Timeline
                                items={detail.operationLogs.map((operationLog) => ({
                                    children: (
                                        <div>
                                            <div className="text-xs text-gray-500">
                                                {formatIsoDateTime(operationLog.operatedAt)}
                                                {'　'}
                                                {operationLog.operatorName ??
                                                    t('partnerControl.penaltyCenter.logs.systemOperator')}
                                            </div>
                                            <div className="text-sm text-gray-900">
                                                {renderOperationLogDesc(operationLog.description, t)}
                                            </div>
                                        </div>
                                    ),
                                }))}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PenaltyDetailPage;
